import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Shield, CreditCard, Save, Loader2, Truck, Phone, Heart, MapPin, ArrowRight, Package, FileText, Upload, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { useThemeContext } from '@/contexts/ThemeContext';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);

export default function DriverProfile() {
  const { token, user } = useAuth();
  const { theme, setTheme } = useThemeContext();
  const [profile, setProfile] = useState(null);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', license_number: '', truck_type: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: '',
  });
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
        Promise.all([api.get('/profile'), api.get('/profile/payments'), api.get('/documents').catch(() => ({ data: [] }))])
      .then(([pRes, payRes, docRes]) => {
        setProfile(pRes.data);
        setPayments(payRes.data);
        setDocuments(docRes.data || []);
        setForm({
          name: pRes.data.name || '',
          phone: pRes.data.phone || '',
          license_number: pRes.data.license_number || '',
          truck_type: pRes.data.truck_type || '',
          emergency_contact_name: pRes.data.emergency_contact_name || '',
          emergency_contact_phone: pRes.data.emergency_contact_phone || '',
          emergency_contact_relation: pRes.data.emergency_contact_relation || '',
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const fields = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== '' && v !== undefined) fields[k] = v;
      });
      const res = await api.put('/profile', fields);
      setProfile(prev => ({ ...prev, ...res.data }));
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="profile-loading">
        <Loader2 className="w-6 h-6 animate-spin text-foreground" />
      </div>
    );
  }

  return (
    <div data-testid="driver-profile">
      <h1 className="text-2xl font-bold tracking-tight mb-1">Profile</h1>
      <p className="text-muted-foreground text-sm mb-4">Your driver information</p>

      <div className="flex gap-6">
        {/* Vertical Side Menu */}
        <nav className="w-44 flex-shrink-0">
          <div className="space-y-1">
            {[
              { id: 'details', icon: User, label: 'Details' },
              { id: 'emergency', icon: Heart, label: 'Emergency' },
              { id: 'payments', icon: CreditCard, label: 'Payments' },
              { id: 'documents', icon: FileText, label: 'Documents' },
              { id: 'appearance', icon: Sun, label: 'Appearance' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
                  activeTab === tab.id
                    ? 'bg-brand-accent/10 text-brand-accent border-l-2 border-brand-accent'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                data-testid={`driver-profile-tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 min-w-0">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">

        {/* Details */}
        <TabsContent value="details">
          <Card className="rounded-xl border-border bg-background mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                <User className="w-4 h-4" /> PERSONAL INFO
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-16 h-16 rounded-full bg-brand-accent/10 flex items-center justify-center text-2xl font-bold text-brand-accent" data-testid="profile-avatar">
                  {form.name ? form.name.charAt(0).toUpperCase() : 'D'}
                </div>
                <div>
                  <p className="font-bold text-lg">{form.name || 'Driver'}</p>
                  <Badge className="bg-brand-accent/10 text-foreground border-border text-xs">{profile?.role?.toUpperCase()}</Badge>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Full Name</label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Your name" className="rounded-xl h-11 mt-1" data-testid="driver-profile-name" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Email</label>
                <Input value={profile?.email || ''} disabled
                  className="rounded-xl h-11 mt-1 bg-muted/50 text-muted-foreground" data-testid="driver-profile-email" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Phone</label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="416-555-0201" className="rounded-xl h-11 mt-1" data-testid="driver-profile-phone" />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl border-border bg-background">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                <Truck className="w-4 h-4" /> CDL & VEHICLE
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground font-medium">CDL License Number</label>
                <Input value={form.license_number} onChange={e => setForm(p => ({ ...p, license_number: e.target.value }))}
                  placeholder="CDL-12345" className="rounded-xl h-11 mt-1" data-testid="driver-profile-license" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Truck Type</label>
                <Input value={form.truck_type} onChange={e => setForm(p => ({ ...p, truck_type: e.target.value }))}
                  placeholder="e.g. Flatbed, Dry Van, Reefer" className="rounded-xl h-11 mt-1" data-testid="driver-profile-truck" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Organization</label>
                <Input value={profile?.organization?.name || ''} disabled
                  className="rounded-xl h-11 mt-1 bg-muted/50 text-muted-foreground" data-testid="driver-profile-org" />
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 flex justify-end">
            <Button onClick={handleSave} disabled={saving}
              className="bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-bold rounded-lg px-6"
              data-testid="driver-profile-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
            </Button>
          </div>
        </TabsContent>

        {/* Emergency Contact */}
        <TabsContent value="emergency">
          <Card className="rounded-xl border-border bg-background">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                <Shield className="w-4 h-4" /> EMERGENCY CONTACT
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Someone to contact in case of emergency</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs text-muted-foreground font-medium">Contact Name</label>
                <Input value={form.emergency_contact_name} onChange={e => setForm(p => ({ ...p, emergency_contact_name: e.target.value }))}
                  placeholder="Full name" className="rounded-xl h-11 mt-1" data-testid="emergency-name" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Contact Phone</label>
                <Input value={form.emergency_contact_phone} onChange={e => setForm(p => ({ ...p, emergency_contact_phone: e.target.value }))}
                  placeholder="416-555-0000" className="rounded-xl h-11 mt-1" data-testid="emergency-phone" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Relationship</label>
                <Input value={form.emergency_contact_relation} onChange={e => setForm(p => ({ ...p, emergency_contact_relation: e.target.value }))}
                  placeholder="e.g. Spouse, Parent, Sibling" className="rounded-xl h-11 mt-1" data-testid="emergency-relation" />
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 flex justify-end">
            <Button onClick={handleSave} disabled={saving}
              className="bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-bold rounded-lg px-6"
              data-testid="emergency-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
            </Button>
          </div>
        </TabsContent>

        {/* Payments */}
        <TabsContent value="payments">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Card className="rounded-xl border-border bg-background">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-emerald-600" data-testid="driver-payments-total">
                  ${payments?.total_spent?.toLocaleString() || 0}
                </p>
                <p className="text-[10px] text-muted-foreground font-bold tracking-wider">TOTAL EARNED</p>
              </CardContent>
            </Card>
            <Card className="rounded-xl border-border bg-background">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-blue-600" data-testid="driver-payments-loads">
                  {payments?.total_loads || 0}
                </p>
                <p className="text-[10px] text-muted-foreground font-bold tracking-wider">LOADS</p>
              </CardContent>
            </Card>
            <Card className="rounded-xl border-border bg-background">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-cyan-600" data-testid="driver-payments-avg">
                  ${payments?.total_loads > 0 ? Math.round(payments.total_spent / payments.total_loads).toLocaleString() : 0}
                </p>
                <p className="text-[10px] text-muted-foreground font-bold tracking-wider">AVG PER LOAD</p>
              </CardContent>
            </Card>
          </div>

          <Card className="rounded-xl border-border bg-background">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground">PAYMENT HISTORY</CardTitle>
            </CardHeader>
            <CardContent>
              {(!payments?.payments || payments.payments.length === 0) ? (
                <div className="text-center py-8 text-muted-foreground text-sm" data-testid="no-driver-payments">
                  No payments received yet
                </div>
              ) : (
                <div className="space-y-3" data-testid="driver-payments-list">
                  {payments.payments.map(load => (
                    <div key={load.load_id} className="flex items-center justify-between p-3 border border-border rounded-xl"
                      data-testid={`driver-payment-${load.load_id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                          <Package className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{load.cargo_description}</p>
                          <div className="flex items-center text-xs text-muted-foreground">
                            <span className="truncate">{load.pickup_name || load.pickup_address?.split(',')[0]}</span>
                            <ArrowRight className="w-3 h-3 mx-1 shrink-0" />
                            <span className="truncate">{load.delivery_name || load.delivery_address?.split(',')[0]}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right ml-2">
                        <p className="text-sm font-bold text-emerald-600">${load.rate?.toLocaleString()}</p>
                        <p className="text-[10px] text-muted-foreground">{milesToKm(load.distance_miles)} km</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents */}
        <TabsContent value="documents">
          <Card className="rounded-xl border-border bg-background mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                <FileText className="w-4 h-4" /> ONBOARDING DOCUMENTS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground mb-2">Upload required documents for onboarding verification</p>
              {['resume', 'drivers_license', 'insurance', 'vehicle_registration', 'annual_safety', 'safety_fitness_certificate', 'medical_certificate', 'abstract'].map(docType => {
                const existing = documents.find(d => d.document_type === docType);
                return (
                  <div key={docType} className="flex items-center justify-between p-3 rounded-lg border border-border bg-surface">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium capitalize">{docType.replace(/_/g, ' ')}</p>
                        {existing && <p className="text-[10px] text-muted-foreground">{existing.file_name || 'Uploaded'}{existing.expiry_date ? ` · Expires ${new Date(existing.expiry_date).toLocaleDateString()}` : ''}</p>}
                      </div>
                    </div>
                    {existing ? (
                      <Badge className={`text-[10px] ${existing.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : existing.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {existing.status?.toUpperCase() || 'PENDING'}
                      </Badge>
                    ) : (
                      <label className="cursor-pointer">
                        <input type="file" accept=".pdf,.jpg,.png,.jpeg" className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploading(true);
                            try {
                              const path = `documents/${user.user_id}_${docType}_${Date.now()}.${file.name.split('.').pop()}`;
                              const { data, error } = await api.post('/documents', {
                                document_type: docType, document_name: file.name, file_url: path, file_name: file.name
                              });
                              if (data) setDocuments(prev => [...prev, data]);
                              toast.success(`${docType.replace(/_/g, ' ')} uploaded`);
                            } catch { toast.error('Upload failed'); }
                            setUploading(false);
                          }} />
                        <span className="flex items-center gap-1 text-xs text-foreground bg-muted px-3 py-1.5 rounded-md hover:bg-muted/80 transition-colors">
                          <Upload className="w-3 h-3" /> Upload
                        </span>
                      </label>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card className="rounded-xl border-border bg-background">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                <Shield className="w-4 h-4" /> INCORPORATION DOCUMENTS
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground mb-2">Upload your business incorporation and compliance documents</p>
              {['articles_of_incorporation', 'business_license', 'hst_gst_registration', 'wsib_certificate', 'master_business_license'].map(docType => {
                const existing = documents.find(d => d.document_type === docType);
                return (
                  <div key={docType} className="flex items-center justify-between p-3 rounded-lg border border-border bg-surface">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium capitalize">{docType.replace(/_/g, ' ')}</p>
                        {existing && <p className="text-[10px] text-muted-foreground">{existing.file_name || 'Uploaded'}{existing.expiry_date ? ` · Expires ${new Date(existing.expiry_date).toLocaleDateString()}` : ''}</p>}
                      </div>
                    </div>
                    {existing ? (
                      <Badge className={`text-[10px] ${existing.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' : existing.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'}`}>
                        {existing.status?.toUpperCase() || 'PENDING'}
                      </Badge>
                    ) : (
                      <label className="cursor-pointer">
                        <input type="file" accept=".pdf,.jpg,.png,.jpeg" className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setUploading(true);
                            try {
                              const path = `documents/${user.user_id}_${docType}_${Date.now()}.${file.name.split('.').pop()}`;
                              const { data } = await api.post('/documents', {
                                document_type: docType, document_name: file.name, file_url: path, file_name: file.name
                              });
                              if (data) setDocuments(prev => [...prev, data]);
                              toast.success(`${docType.replace(/_/g, ' ')} uploaded`);
                            } catch { toast.error('Upload failed'); }
                            setUploading(false);
                          }} />
                        <span className="flex items-center gap-1 text-xs text-foreground bg-muted px-3 py-1.5 rounded-md hover:bg-muted/80 transition-colors">
                          <Upload className="w-3 h-3" /> Upload
                        </span>
                      </label>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance */}
        <TabsContent value="appearance">
          <Card className="rounded-xl border-border bg-background">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-bold tracking-wider text-muted-foreground flex items-center gap-2">
                <Sun className="w-4 h-4" /> APPEARANCE
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Choose your preferred theme</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <button onClick={() => setTheme('light')}
                  className={`p-6 rounded-xl border-2 text-center transition-all ${
                    theme === 'light' ? 'border-brand-accent bg-brand-accent/10 ring-2 ring-brand-accent' : 'border-border hover:border-muted-foreground'
                  }`}>
                  <Sun className="w-8 h-8 mx-auto mb-3 text-amber-500" />
                  <p className="font-bold">Light Mode</p>
                  <p className="text-xs text-muted-foreground mt-1">Clean, bright interface</p>
                </button>
                <button onClick={() => setTheme('dark')}
                  className={`p-6 rounded-xl border-2 text-center transition-all ${
                    theme === 'dark' ? 'border-brand-accent bg-brand-accent/10 ring-2 ring-brand-accent' : 'border-border hover:border-muted-foreground'
                  }`}>
                  <Moon className="w-8 h-8 mx-auto mb-3 text-indigo-400" />
                  <p className="font-bold">Dark Mode</p>
                  <p className="text-xs text-muted-foreground mt-1">Easy on the eyes</p>
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </div>{/* end content */}
      </div>{/* end flex */}
    </div>
  );
}
